import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, IconButton } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Button, NumberField, DatePicker } from 'm2m.components';
import { Close } from '@material-ui/icons';
import moment, { Moment } from 'moment';
import { HistoricoAtualizacaoCota } from 'common/genericTypes';

interface ModalFormularioProps {
  open: boolean;
  onClose: () => void;
  onSave: (dados: FormularioDados) => void;
  dadosEdicao?: HistoricoAtualizacaoCota | null;
  loading?: boolean;
  minDate?: Moment | null;
  datasInvalidas?: Moment[];
}

export interface FormularioDados {
  dataReferencia: string;
  valorCota: number;
  patrimonioLiquido: number;
  rentabilidadeDia: number;
  rentabilidadeMes: number;
  rentabilidadeAno: number;
}

const ModalFormulario: React.FC<ModalFormularioProps> = ({
  open,
  onClose,
  onSave,
  dadosEdicao,
  loading = false,
  minDate,
  datasInvalidas = [],
}) => {
  const [dataReferencia, setDataReferencia] = useState<Moment | null>(null);
  const [valorCota, setValorCota] = useState<number>(0);
  const [patrimonioLiquido, setPatrimonioLiquido] = useState<number>(0);
  const [rentabilidadeDia, setRentabilidadeDia] = useState<number>(0);
  const [rentabilidadeMes, setRentabilidadeMes] = useState<number>(0);
  const [rentabilidadeAno, setRentabilidadeAno] = useState<number>(0);
  const [edicao, setEdicao] = useState<boolean>(false);

  useEffect(() => {
    if (dadosEdicao) {
      setDataReferencia(moment(dadosEdicao.dataReferencia));
      setValorCota(dadosEdicao.valorCota);
      setPatrimonioLiquido(dadosEdicao.patrimonioLiquido);
      setRentabilidadeDia(dadosEdicao.rentabilidadeDia);
      setRentabilidadeMes(dadosEdicao.rentabilidadeMes);
      setRentabilidadeAno(dadosEdicao.rentabilidadeAno);
      setEdicao(true);
    } else {
      limparCampos();
      handleMinDate();
    }
  }, [dadosEdicao, open]);

  const handleMinDate = () => {
    if (!minDate) return;
    
    let proximaData = minDate.clone();
    const dataLimite = moment().add(10, 'days');
    
    while (proximaData.isBefore(dataLimite)) {
      const dataInvalida = datasInvalidas.some((dataInv) => dataInv.isSame(proximaData, 'day'));
      
      if (!dataInvalida) {
        setDataReferencia(proximaData);
        return;
      }
      
      proximaData.add(1, 'day');
    }
  }

  const limparCampos = () => {
    setEdicao(false);
    setDataReferencia(null);
    setValorCota(0);
    setPatrimonioLiquido(0);
    setRentabilidadeDia(0);
    setRentabilidadeMes(0);
    setRentabilidadeAno(0);
  };

  const handleSave = () => {
    if (!dataReferencia) return;
    
    const dados: FormularioDados = {
      dataReferencia: dataReferencia.format('YYYY-MM-DD'),
      valorCota,
      patrimonioLiquido,
      rentabilidadeDia,
      rentabilidadeMes,
      rentabilidadeAno,
    };

    onSave(dados);
  };

  const handleClose = () => {
    limparCampos();
    onClose();
  };

  const shouldDisableDate = (data: any) => {
    return datasInvalidas.some((dataInvalida) => dataInvalida.isSame(data, 'day'));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{dadosEdicao ? 'Editar Valores da Cota' : 'Novo Registro de Valores'}</span>
          <IconButton size="small" onClick={handleClose}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        {!edicao && (
          <Alert severity="info" style={{ marginBottom: 16 }}>
            <strong>Importante:</strong> Os valores devem ser lançados sequencialmente, da menor data para a maior data, para garantir a integridade dos dados históricos do fundo.
          </Alert>
        )}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Data de Referência *"
              value={dataReferencia}
              onChange={(date: Moment | null) => setDataReferencia(date)}
              disableFuture
              disabled={edicao}
              shouldDisableDate={shouldDisableDate}
              minDate={edicao ? undefined : moment(minDate)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <NumberField
              label="Valor da Cota *"
              value={valorCota}
              onChange={(value: number) => setValorCota(value)}
              numberType="eightDecimals"
              helperText="Até 8 casas decimais"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <NumberField
              label="Patrimônio Líquido *"
              value={patrimonioLiquido}
              onChange={(value: number) => setPatrimonioLiquido(value)}
              adornment="R$"
              adornmentPosition="start"
              numberType="money"
              helperText="Até 2 casas decimais"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <NumberField
              label="Rentabilidade ao Dia (%) *"
              value={rentabilidadeDia}
              onChange={(value: number) => setRentabilidadeDia(value)}
              adornment={'%'}
              adornmentPosition={'end'}
              numberType="eightDecimals"
              helperText="Até 8 casas decimais"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <NumberField
              label="Rentabilidade ao Mês (%) *"
              value={rentabilidadeMes}
              onChange={(value: number) => setRentabilidadeMes(value)}
              adornment={'%'}
              adornmentPosition={'end'}
              numberType="eightDecimals"
              helperText="Até 8 casas decimais"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <NumberField
              label="Rentabilidade ao Ano (%) *"
              value={rentabilidadeAno}
              onChange={(value: number) => setRentabilidadeAno(value)}
              adornment={'%'}
              adornmentPosition={'end'}
              numberType="eightDecimals"
              helperText="Até 8 casas decimais"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave} disabled={loading || !dataReferencia}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalFormulario;
